import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import IconButton from "../../../../components/base/IconButton";
import { BottomSheet } from "../../../../components/base/BottomSheet";
import FilteringMobile, { FilterSelections, filterSelectionsToQuery } from "../../../../components/data/FilteringMobile";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import { JobsChartSegments, SortTypes } from "../../../../core/Enums";
import {
    clearPageName, setJobsReportBody, setMobileToolbar, setPage,
    useGlobal, useHub, useJobsReportBody
} from "../../../../core/Global";
import DataItem from "../../../../models/DataItem";
import { JobsReportSort } from "../models/JobsReportRequest";
import JobReportModel, { DataColumnsVisibility, JobReportDataRow, JobReportTechPaymentRow, JobsColumnsVisibility, TechPayColumnsVisibility } from "../models/JobReportModel";
import Job from "../../../crm/jobs/models/Job";
import JobReportSendDrawer from "../components/JobReportSendDrawer";
import JobsReportTables from "../components/JobsReportTables";
import JobsReportDrillDrawer from "../components/JobsReportDrillDrawer";
import FilteringPropsAgents from "../../../crm/jobs/filtering/filterings-agents.json";
import FilteringPropsTechs from "../../../crm/jobs/filtering/filterings-techs.json";
import FilteringPropsSources from "../../../crm/jobs/filtering/filterings-sources.json";
import FilteringPropsTypes from "../../../crm/jobs/filtering/filterings-types.json";
import FilteringPropsMetros from "../../../crm/jobs/filtering/filterings-metros.json";
import FilteringPropsPayments from "../../../crm/jobs/filtering/filterings-payments.json";
import FilteringPropsPaymentsDetails from "../../../crm/jobs/filtering/filterings-payments-details.json";
import FilteringPropsClosedJobs from "../../../crm/jobs/filtering/filterings-closedjobs.json";
import "./JobsReportMobile.css";


const moneyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const slugToSegment = (s?: string): JobsChartSegments | undefined => {
    if (!s) return undefined;
    switch (s.toLowerCase()) {
        case "dispatcher": return JobsChartSegments.Dispatchers;
        case "technician": return JobsChartSegments.Technician;
        case "source": return JobsChartSegments.JobSource;
        case "type": return JobsChartSegments.JobType;
        case "metro": return JobsChartSegments.Metro;
        case "payments": return JobsChartSegments.Payments;
        case "closed": return JobsChartSegments.ClosedJobs;
        case "canceled": return JobsChartSegments.CanceledJobs;
    }
    return undefined;
};

const DATE_EL_ID = "jobs-report-mobile-date";

// Simple settings-style row with an on/off toggle switch, used in the Columns drawer.
// The whole row is tappable; the switch just reflects state (readOnly).
const ColToggle = (p: { label?: string; on?: boolean; onToggle: () => void }) => (
    <div className="jrm-col-row" onClick={p.onToggle}>
        <span className="jrm-col-row-label">{p.label}</span>
        <div className="form-check form-switch form-check-custom form-check-solid">
            <input className="form-check-input" type="checkbox" checked={!!p.on} readOnly />
        </div>
    </div>
);


// Mobile-only Jobs Report. Renders inside ReportsMainView's outlet (which already provides
// Layout/Section/ScrollContainer + the mobile report switcher). The desktop JobsReport is
// intentionally left untouched — this is a separate, card-based, phone-friendly view.
export default function JobsReportMobile() {

    const hub = useHub();
    const { segment } = useParams();
    const [searchParams] = useSearchParams();

    // useGlobal() subscribes this component so it re-renders when jobsReportBody
    // (or window size) changes; useJobsReportBody() reads the current value. Both are
    // hook-named so no plain const sits between hooks (repo's rules-of-hooks quirk).
    useGlobal();
    const body = useJobsReportBody();

    const [model, modelSet] = useState<JobReportModel>();
    const [data, dataSet] = useState<JobReportDataRow[]>();
    const [jobs, jobsSet] = useState<Job[]>();
    const [payments, paymentsSet] = useState<JobReportTechPaymentRow[]>();
    const [avTickets, avTicketsSet] = useState<number>();

    const [loading, loadingSet] = useState<boolean>(true);
    const [firstLoad, firstLoadSet] = useState<boolean>(true);

    const [dates, datesSet] = useState<DataItem[]>([]);
    const [filterQuery, filterQuerySet] = useState<string>();
    const [filterDrawer, filterDrawerSet] = useState<boolean>(false);
    const [chartDates, chartDatesSet] = useState<any>();
    const [sendModal, sendModalSet] = useState<boolean>(false);

    // Applied filter selections (drive filterQuery) + the working draft edited in the drawer.
    const [filterSelections, filterSelectionsSet] = useState<FilterSelections>({});
    const [filterDraft, filterDraftSet] = useState<FilterSelections>({});

    // Column visibility (shared with JobsReportTables via props; toggled from the filter drawer).
    const [dataColsVisibility, dataColsVisibilitySet] = useState<DataColumnsVisibility>();
    const [dataStatusCols, dataStatusColsSet] = useState<string[]>([]);
    const [jobsColsVisibility, jobsColsVisibilitySet] = useState<JobsColumnsVisibility>();
    const [techColsVisibility, techColsVisibilitySet] = useState<TechPayColumnsVisibility>();

    const [columnsDrawer, columnsDrawerSet] = useState<boolean>(false);
    const [dateSheet, dateSheetSet] = useState<boolean>(false);
    const [drillItem, drillItemSet] = useState<{ title: string; value: string; avatar?: string }>();

    const filterDrawerRef = useRef<DrawerRef>(null);
    const columnsDrawerRef = useRef<DrawerRef>(null);

    // Count of currently-enabled columns for the table in view (drives the header badge).
    const columnsOnCount = useMemo(() => {
        const countTrue = (o?: any) => o ? Object.values(o).filter(v => v === true).length : 0;
        if (jobs && jobs.length > 0) return countTrue(jobsColsVisibility);
        if (payments && payments.length > 0) return countTrue(techColsVisibility);
        if (data && data.length > 0) return countTrue(dataColsVisibility) + dataStatusCols.length;
        return 0;
    }, [jobs, payments, data, jobsColsVisibility, techColsVisibility, dataColsVisibility, dataStatusCols]);

    // Whether any filter is currently applied (drives the blue dot on the Filter icon).
    const hasFilters = useMemo(
        () => Object.keys(filterSelections).some(k => (filterSelections[k]?.length ?? 0) > 0),
        [filterSelections]);

    useEffect(() => {
        hub.Get("/reports/jobs/dates", res => datesSet(res.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reflect the current segment in the mobile header title.
    useEffect(() => {
        if (body) setPage(segmentTitle());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body?.segment]);

    useEffect(() => {
        return () => { clearPageName(); };
    }, []);

    // Columns + Filter + Send icon buttons in the mobile header action bar. Re-set whenever
    // the enabled-column count changes so the Columns badge stays live (no cleanup here to
    // avoid a flash; the unmount cleanup is a separate effect below).
    useEffect(() => {
        setMobileToolbar(Toolbar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsOnCount, hasFilters]);

    useEffect(() => {
        return () => setMobileToolbar(undefined);
    }, []);

    // Seed the drawer's working draft from the applied selections every time it opens, so a
    // reopened filter drawer always shows (and lets you continue editing) the current filter.
    useEffect(() => {
        if (filterDrawer) filterDraftSet(filterSelections);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDrawer]);

    // Keep body.segment in sync with the :segment url param (driven by the switcher).
    useEffect(() => {
        if (!body) return;
        const seg = slugToSegment(segment);
        if (seg !== undefined && seg !== body.segment) {
            loadingSet(true);
            filterQuerySet(undefined);
            filterSelectionsSet({});
            filterDraftSet({});
            setJobsReportBody({ ...body, segment: seg, segmentIds: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segment, body?.segment]);

    useEffect(() => {
        if (body) fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body, filterQuery]);

    // Custom date range picker (dateId === "99"); the picker input sits at the top of the report.
    useEffect(() => {
        if (body?.dateId === "99") {
            window.daterangecustom(DATE_EL_ID, (start, end) => {
                chartDatesSet({ start: start.format("DD/MM/YYYY"), end: end.format("DD/MM/YYYY") });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body?.dateId]);

    useEffect(() => {
        if (chartDates && body) {
            setJobsReportBody({ ...body, startDate: chartDates.start, toDate: chartDates.end });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartDates]);

    // --- Column visibility init (shared localStorage keys with desktop) ---
    useEffect(() => {
        if (!body?.segment) return;
        if (!data || data.length < 1) return;
        const str = window.localStorage.getItem("jobs_reports_dataColsVisibility_" + body.segment.toString());
        if (str) {
            const dcv: DataColumnsVisibility = JSON.parse(str);
            dataColsVisibilitySet(dcv);
            dataStatusColsSet(dcv.statuses ?? []);
        } else {
            const stIds: string[] = [];
            data[0].statusCounts.forEach(s => stIds.push(s.id!));
            dataColsVisibilitySet({ avgtick: true, convrate: true, gross: true, jobs: true, profit: true });
            dataStatusColsSet(stIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body?.segment, data]);

    useEffect(() => {
        if (!body?.segment) return;
        const jcv = window.localStorage.getItem("jobs_reports_jobsColsVisibility_" + body.segment.toString());
        if (jcv) jobsColsVisibilitySet(JSON.parse(jcv));
        else if (body.segment === JobsChartSegments.CanceledJobs) {
            jobsColsVisibilitySet({ name: true, address: true, type: true, source: true, tech: true, createdDate: true, lastUpdate: true, cancelNote: true });
        } else {
            jobsColsVisibilitySet({ name: true, address: true, type: true, source: true, createdDate: true, lastUpdate: true, total: true, cash: true, ccmanual: true, cccharge: true, check: true, coParts: true, techParts: true, coProfits: true, techProfits: true, sourceProfits: true, techBalance: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body?.segment]);

    useEffect(() => {
        if (!body?.segment) return;
        const tcv = window.localStorage.getItem("jobs_reports_techColsVisibility_" + body.segment.toString());
        if (tcv) techColsVisibilitySet(JSON.parse(tcv));
        else techColsVisibilitySet({ cash: true, check: true, closedJobs: true, metro: true, techBalance: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body?.segment]);

    // --- Column visibility persist ---
    useEffect(() => {
        if (dataColsVisibility && body?.segment) {
            const v = { ...dataColsVisibility, statuses: dataStatusCols };
            window.localStorage.setItem("jobs_reports_dataColsVisibility_" + body.segment.toString(), JSON.stringify(v));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataColsVisibility, dataStatusCols]);

    useEffect(() => {
        if (jobsColsVisibility && body?.segment)
            window.localStorage.setItem("jobs_reports_jobsColsVisibility_" + body.segment.toString(), JSON.stringify(jobsColsVisibility));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobsColsVisibility]);

    useEffect(() => {
        if (techColsVisibility && body?.segment)
            window.localStorage.setItem("jobs_reports_techColsVisibility_" + body.segment.toString(), JSON.stringify(techColsVisibility));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [techColsVisibility]);


    const sortBody: JobsReportSort = { sortKey: "", sortType: SortTypes.Descending };

    const fetch = () => {
        if (!body) return;

        loadingSet(true);

        let q = "?a=1";
        if (firstLoad && searchParams) q += "&" + searchParams.toString();
        if (filterQuery) q += "&" + filterQuery;

        hub.Post("/reports/jobs/chart/" + q, {
            ...body,
            ...sortBody
        }, res => {
            if (res.success) {
                modelSet(res.data);
                dataSet(res.data.data);
                jobsSet(res.data.jobs);
                paymentsSet(res.data.techPayments);

                const totalRow = (res.data.data as JobReportDataRow[] | undefined)?.find(d => d.id === "total");
                avTicketsSet(totalRow?.averageTickets);

                setTimeout(() => {
                    loadingSet(false);
                    firstLoadSet(false);
                }, 300);
            }
        });
    };

    const segmentTitle = (s?: JobsChartSegments): string => {
        const seg = s ?? body?.segment;
        if (seg === JobsChartSegments.Dispatchers) return "Dispatcher";
        if (seg === JobsChartSegments.Technician) return "Technician";
        if (seg === JobsChartSegments.JobSource) return "Job Source";
        if (seg === JobsChartSegments.JobType) return "Job Type";
        if (seg === JobsChartSegments.Metro) return "Metro";
        if (seg === JobsChartSegments.Payments) return "Payments";
        if (seg === JobsChartSegments.ClosedJobs) return "Closed Jobs";
        if (seg === JobsChartSegments.CanceledJobs) return "Canceled Jobs";
        return "Report";
    };

    const filteringProps = () => {
        if (!body) return [];
        switch (body.segment) {
            case JobsChartSegments.Dispatchers: return FilteringPropsAgents;
            case JobsChartSegments.Technician: return FilteringPropsTechs;
            case JobsChartSegments.JobSource: return FilteringPropsSources;
            case JobsChartSegments.JobType: return FilteringPropsTypes;
            case JobsChartSegments.Metro: return FilteringPropsMetros;
            case JobsChartSegments.Payments:
                return body.segmentIds.length > 0 ? FilteringPropsPaymentsDetails : FilteringPropsPayments;
            case JobsChartSegments.ClosedJobs: return FilteringPropsClosedJobs;
            case JobsChartSegments.CanceledJobs: return FilteringPropsClosedJobs;
            default: return [];
        }
    };

    // Mobile header action bar: Columns (with live count badge) + Filter + Send.
    const Toolbar = () => (
        <FlexRow>
            <div className="position-relative">
                <IconButton icon="table-columns" type="regular" size={19} gap={12} title="Columns"
                    onClick={() => columnsDrawerSet(true)} />
                {columnsOnCount > 0 && <span className="jrm-col-badge">{columnsOnCount}</span>}
            </div>
            <div className="position-relative">
                <IconButton icon="filter" type="regular" size={19} gap={12} title="Filters"
                    onClick={() => filterDrawerSet(true)} />
                {hasFilters && <span className="jrm-filter-dot" />}
            </div>
            <IconButton icon="arrow-up-from-square" type="regular" size={19} gap={12} title="Send Report"
                onClick={() => sendModalSet(true)} />
        </FlexRow>
    );

    if (!body) {
        return (
            <div className="p-10 text-center">
                <Indicator />
            </div>
        );
    }

    const showOverview =
        body.segment !== JobsChartSegments.CanceledJobs &&
        body.segment !== JobsChartSegments.ClosedJobs &&
        body.segment !== JobsChartSegments.Payments &&
        body.segmentIds.length === 0;

    return (
        <div className="jobs-report-mobile p-4">

            {/* Report date range — always at the top, above the stat boxes */}
            <div className="mb-4">
                <button type="button" className="jrm-date-select" onClick={() => dateSheetSet(true)}>
                    <Icon icon="calendar-range" type="solid" size={15} className="me-3 text-primary" />
                    <span className="jrm-date-select-label">
                        {dates.find(d => d.id === body.dateId)?.name ?? "Select date range"}
                    </span>
                    <Icon icon="chevron-down" size={13} className="ms-auto text-gray-500" />
                </button>
                {
                    body.dateId === "99" &&
                    <div className="input-group input-group-sm mt-2" id={DATE_EL_ID}>
                        <input type="text" className="form-control form-control-sm" readOnly
                            value={(body.startDate ?? "") + " - " + (body.toDate ?? "")} />
                        <span className="input-group-text"><Icon icon="calendar" /></span>
                    </div>
                }
            </div>

            {
                loading ?
                    <div className="py-20"><Indicator /></div>
                    :
                    <>
                        {/* Overview: KPIs on top, then flattened status bar, then legend */}
                        {
                            showOverview && model?.pieChart &&
                            <div className="card mb-4">
                                <div className="card-body">

                                    {/* KPIs */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-6">
                                            <div className="jrm-kpi bg-light-info">
                                                <div className="fs-2 fw-bolder text-info lh-1">{model.jobsToClosedRate ?? 0}%</div>
                                                <div className="fs-8 fw-bold text-gray-600 mt-1">Jobs to Closed CR</div>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="jrm-kpi bg-light-danger">
                                                <div className="fs-2 fw-bolder text-danger lh-1">
                                                    {moneyFormatter.format(Number.isFinite(avTickets) ? avTickets! : 0)}
                                                </div>
                                                <div className="fs-8 fw-bold text-gray-600 mt-1">Average Tickets</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Flattened donut = single stacked bar */}
                                    <FlexRow className="mb-2">
                                        <span className="fs-8 fw-bold text-gray-500 text-uppercase">Total Jobs</span>
                                        <span className="ms-auto fs-3 fw-bolder text-gray-900 lh-1">{model.pieChart.total}</span>
                                    </FlexRow>
                                    <div className="jrm-bar mb-4">
                                        {
                                            (model.pieChart.total ?? 0) > 0 ?
                                                model.pieChart.statusCounts.filter(s => s.count > 0).map((st, k) =>
                                                    <div key={k}
                                                        style={{ flexGrow: st.count, background: st.color }}
                                                        title={`${st.name}: ${st.count} (${st.percent}%)`} />
                                                )
                                                :
                                                <div style={{ flexGrow: 1, background: "var(--bs-gray-200, #e4e6ef)" }} />
                                        }
                                    </div>

                                    {/* Status legend */}
                                    <div className="row g-2">
                                        {
                                            model.pieChart.statusCounts.map((st, k) =>
                                                <div key={k} className="col-6">
                                                    <FlexRow alignItems="start" className="jrm-legend-item">
                                                        <Icon icon="circle" type="solid" color={st.color} size={10} className="mt-1 me-2" />
                                                        <div>
                                                            <div className="fs-8 fw-bold text-gray-700 lh-1">{st.name}</div>
                                                            <FlexRow className="mt-1">
                                                                <span className="fs-7 fw-bolder text-gray-900">{st.count}</span>
                                                                <span className="fs-8 text-gray-400 ms-1">({st.percent}%)</span>
                                                            </FlexRow>
                                                        </div>
                                                    </FlexRow>
                                                </div>
                                            )
                                        }
                                    </div>

                                </div>
                            </div>
                        }

                        {/* Results as the exact desktop tables — data / jobs / payments, horizontal scroll */}
                        <JobsReportTables body={body} data={data} jobs={jobs} payments={payments}
                            filterQuery={filterQuery} onRefetch={fetch} onDrill={drillItemSet}
                            dataColsVisibility={dataColsVisibility} dataStatusCols={dataStatusCols}
                            jobsColsVisibility={jobsColsVisibility} techColsVisibility={techColsVisibility} />
                    </>
            }

            <div className="h-100px"></div>

            {
                sendModal &&
                <JobReportSendDrawer
                    body={body}
                    sort={sortBody}
                    query={filterQuery}
                    onClose={() => sendModalSet(false)} />
            }

            {
                filterDrawer &&
                <Drawer ref={filterDrawerRef} id="drawer-jobs-report-filter" title="Filters" size="medium"
                    onClose={() => filterDrawerSet(false)}
                    toolbarComponent={() => (
                        <button type="button" className="btn btn-sm btn-primary" disabled={loading}
                            onClick={() => {
                                filterSelectionsSet(filterDraft);
                                filterQuerySet(filterSelectionsToQuery(filterDraft));
                                filterDrawerRef.current?.close();
                            }}>
                            <Icon icon={loading ? "spinner-third" : "check"} spin={loading} className="me-2" />
                            Apply
                        </button>
                    )}>
                    <div className="p-2">
                        <FilteringMobile
                            filteringProps={filteringProps()}
                            apiRoute={`/reports/jobs/data/${body.segment}/`}
                            value={filterDraft}
                            onChange={filterDraftSet} />
                    </div>
                </Drawer>
            }

            {
                columnsDrawer &&
                <Drawer ref={columnsDrawerRef} id="drawer-jobs-report-columns" title="Columns" size="medium"
                    onClose={() => columnsDrawerSet(false)}
                    toolbarComponent={() => (
                        <button type="button" className="btn btn-sm btn-primary"
                            onClick={() => columnsDrawerRef.current?.close()}>
                            Apply
                        </button>
                    )}>
                    <div className="p-2">

                        <div className="text-gray-500 fs-8 mb-4">Turn table columns on or off.</div>

                        {
                            data && data.length > 0 &&
                            <div className="jrm-cols-wrap">
                                <ColToggle label="Total Jobs" on={dataColsVisibility?.jobs}
                                    onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, jobs: !dataColsVisibility?.jobs })} />
                                {
                                    data[0].statusCounts.map((st, k) =>
                                        <ColToggle key={k} label={st.name} on={dataStatusCols.includes(st.id!)}
                                            onToggle={() => {
                                                const v = [...dataStatusCols];
                                                const i = v.indexOf(st.id!);
                                                if (i < 0) v.push(st.id!); else v.splice(i, 1);
                                                dataStatusColsSet(v);
                                            }} />
                                    )
                                }
                                <ColToggle label="Conv Rate" on={dataColsVisibility?.convrate}
                                    onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, convrate: !dataColsVisibility?.convrate })} />
                                <ColToggle label="Avg Tickets" on={dataColsVisibility?.avgtick}
                                    onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, avgtick: !dataColsVisibility?.avgtick })} />
                                <ColToggle label="Total Gross" on={dataColsVisibility?.gross}
                                    onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, gross: !dataColsVisibility?.gross })} />
                                {
                                    body.segment === JobsChartSegments.JobSource &&
                                    <ColToggle label="Source Profit" on={dataColsVisibility?.sourceprofit}
                                        onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, sourceprofit: !dataColsVisibility?.sourceprofit })} />
                                }
                                <ColToggle label="Total Profit" on={dataColsVisibility?.profit}
                                    onToggle={() => dataColsVisibilitySet({ ...dataColsVisibility, profit: !dataColsVisibility?.profit })} />
                            </div>
                        }

                        {
                            payments && payments.length > 0 &&
                            <div className="jrm-cols-wrap">
                                <ColToggle label="Phone" on={techColsVisibility?.phone} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, phone: !techColsVisibility?.phone })} />
                                <ColToggle label="Metros" on={techColsVisibility?.metro} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, metro: !techColsVisibility?.metro })} />
                                <ColToggle label="Types" on={techColsVisibility?.type} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, type: !techColsVisibility?.type })} />
                                <ColToggle label="Sources" on={techColsVisibility?.source} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, source: !techColsVisibility?.source })} />
                                <ColToggle label="Closed Jobs" on={techColsVisibility?.closedJobs} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, closedJobs: !techColsVisibility?.closedJobs })} />
                                <ColToggle label="Total" on={techColsVisibility?.total} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, total: !techColsVisibility?.total })} />
                                <ColToggle label="Cash" on={techColsVisibility?.cash} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, cash: !techColsVisibility?.cash })} />
                                <ColToggle label="CC Charge" on={techColsVisibility?.cccharge} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, cccharge: !techColsVisibility?.cccharge })} />
                                <ColToggle label="CC Manual" on={techColsVisibility?.ccmanual} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, ccmanual: !techColsVisibility?.ccmanual })} />
                                <ColToggle label="Check" on={techColsVisibility?.check} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, check: !techColsVisibility?.check })} />
                                <ColToggle label="E-Pay" on={techColsVisibility?.epay} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, epay: !techColsVisibility?.epay })} />
                                <ColToggle label="Rinvox Pay" on={techColsVisibility?.rinvoxPay} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, rinvoxPay: !techColsVisibility?.rinvoxPay })} />
                                <ColToggle label="Balance" on={techColsVisibility?.techBalance} onToggle={() => techColsVisibilitySet({ ...techColsVisibility, techBalance: !techColsVisibility?.techBalance })} />
                            </div>
                        }

                        {
                            jobs && jobs.length > 0 &&
                            <div className="jrm-cols-wrap">
                                <ColToggle label="Status" on={jobsColsVisibility?.status} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, status: !jobsColsVisibility?.status })} />
                                <ColToggle label="Name" on={jobsColsVisibility?.name} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, name: !jobsColsVisibility?.name })} />
                                <ColToggle label="Phone" on={jobsColsVisibility?.phone} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, phone: !jobsColsVisibility?.phone })} />
                                <ColToggle label="Job Type" on={jobsColsVisibility?.type} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, type: !jobsColsVisibility?.type })} />
                                <ColToggle label="Job Source" on={jobsColsVisibility?.source} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, source: !jobsColsVisibility?.source })} />
                                <ColToggle label="Address" on={jobsColsVisibility?.address} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, address: !jobsColsVisibility?.address })} />
                                <ColToggle label="Metro" on={jobsColsVisibility?.metro} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, metro: !jobsColsVisibility?.metro })} />
                                <ColToggle label="Tech" on={jobsColsVisibility?.tech} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, tech: !jobsColsVisibility?.tech })} />
                                <ColToggle label="Created At" on={jobsColsVisibility?.createdDate} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, createdDate: !jobsColsVisibility?.createdDate })} />
                                <ColToggle label={body.segment === JobsChartSegments.CanceledJobs ? "Canceled At" : "Closed At"} on={jobsColsVisibility?.lastUpdate} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, lastUpdate: !jobsColsVisibility?.lastUpdate })} />
                                <ColToggle label="Appt Date" on={jobsColsVisibility?.apptDate} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, apptDate: !jobsColsVisibility?.apptDate })} />
                                {
                                    body.segment === JobsChartSegments.CanceledJobs ?
                                        <ColToggle label="Note" on={jobsColsVisibility?.cancelNote} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, cancelNote: !jobsColsVisibility?.cancelNote })} />
                                        :
                                        <>
                                            <ColToggle label="Inv Number" on={jobsColsVisibility?.invNo} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, invNo: !jobsColsVisibility?.invNo })} />
                                            <ColToggle label="Total" on={jobsColsVisibility?.total} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, total: !jobsColsVisibility?.total })} />
                                            <ColToggle label="Cash" on={jobsColsVisibility?.cash} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, cash: !jobsColsVisibility?.cash })} />
                                            <ColToggle label="CC Manual" on={jobsColsVisibility?.ccmanual} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, ccmanual: !jobsColsVisibility?.ccmanual })} />
                                            <ColToggle label="CC Charge" on={jobsColsVisibility?.cccharge} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, cccharge: !jobsColsVisibility?.cccharge })} />
                                            <ColToggle label="Check" on={jobsColsVisibility?.check} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, check: !jobsColsVisibility?.check })} />
                                            <ColToggle label="E-Pay" on={jobsColsVisibility?.epay} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, epay: !jobsColsVisibility?.epay })} />
                                            <ColToggle label="Rinvox Pay" on={jobsColsVisibility?.rinvoxPay} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, rinvoxPay: !jobsColsVisibility?.rinvoxPay })} />
                                            <ColToggle label="Tech Parts" on={jobsColsVisibility?.techParts} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, techParts: !jobsColsVisibility?.techParts })} />
                                            <ColToggle label="CO Parts" on={jobsColsVisibility?.coParts} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, coParts: !jobsColsVisibility?.coParts })} />
                                            <ColToggle label="Tax" on={jobsColsVisibility?.tax} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, tax: !jobsColsVisibility?.tax })} />
                                            <ColToggle label="Tech Profit" on={jobsColsVisibility?.techProfits} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, techProfits: !jobsColsVisibility?.techProfits })} />
                                            <ColToggle label="Source Profit" on={jobsColsVisibility?.sourceProfits} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, sourceProfits: !jobsColsVisibility?.sourceProfits })} />
                                            <ColToggle label="CO Profit" on={jobsColsVisibility?.coProfits} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, coProfits: !jobsColsVisibility?.coProfits })} />
                                            <ColToggle label="Tech Fee" on={jobsColsVisibility?.techFee} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, techFee: !jobsColsVisibility?.techFee })} />
                                            <ColToggle label="Tech Balance" on={jobsColsVisibility?.techBalance} onToggle={() => jobsColsVisibilitySet({ ...jobsColsVisibility, techBalance: !jobsColsVisibility?.techBalance })} />
                                        </>
                                }
                            </div>
                        }

                    </div>
                </Drawer>
            }

            <BottomSheet id="report-date-sheet" open={dateSheet} onClose={() => dateSheetSet(false)}>
                <div className="reports-date-sheet">
                    <div className="fs-4 fw-bolder text-gray-900 text-center mb-4">Date Range</div>
                    {
                        dates.map((d, k) => {
                            const active = body.dateId === d.id;
                            return (
                                <div key={k}
                                    className={"d-flex align-items-center px-3 py-3 rounded mb-1 cursor-pointer " + (active ? "bg-light-primary" : "")}
                                    onClick={() => {
                                        setJobsReportBody({ ...body, dateId: d.id! });
                                        dateSheetSet(false);
                                    }}>
                                    <span className="symbol symbol-35px me-3">
                                        <span className={"symbol-label " + (active ? "bg-primary" : "bg-light")}>
                                            <Icon icon="calendar" type="solid" size={14}
                                                className={active ? "text-white" : "text-gray-600"} />
                                        </span>
                                    </span>
                                    <span className={"fs-5 " + (active ? "fw-bolder text-primary" : "fw-bold text-gray-800")}>
                                        {d.name}
                                    </span>
                                    {active && <Icon icon="check" type="solid" size={16} className="ms-auto text-primary" />}
                                </div>
                            );
                        })
                    }
                </div>
            </BottomSheet>

            {
                drillItem &&
                <JobsReportDrillDrawer key={drillItem.value}
                    body={body} item={drillItem} filterQuery={filterQuery}
                    jobsColsVisibility={jobsColsVisibility}
                    onClose={() => drillItemSet(undefined)} />
            }

        </div>
    );
}
